import React from "react";

import type { User } from "~/common/types";
import { useAuth } from "~/features/authn/context";
import { useTenant } from "~/features/tenant/context";

export const HASURA_EDITOR_ROLES = ["editor", "admin"];

export const POLICIES = {
  "changeRequests:batchApprove": (user: User) => user.role === "admin",
  "references:deactivate": (user: User) => user.role === "admin",
};

export function useIsActiveRolloAdmin(): boolean {
  const { user } = useAuth();
  const { tenantShortCode } = useTenant();
  return tenantShortCode === "rollo" && user?.role === "admin";
}

export const useAuthorization = () => {
  const { user } = useAuth();
  if (!user) {
    throw Error("User does not exist!");
  }

  const checkAccess = React.useCallback(
    ({ allowedRoles }: { allowedRoles: string[] }) => {
      if (allowedRoles && allowedRoles.length > 0) {
        return allowedRoles?.includes(user.role);
      }
      return true;
    },
    [user.role],
  );
  return { checkAccess, role: user.role };
};

type AuthorizationProps = {
  forbiddenFallback?: React.ReactNode;
  children: React.ReactNode;
} & (
  | {
      allowedRoles: string[];
      policyCheck?: never;
    }
  | {
      allowedRoles?: never;
      policyCheck: boolean;
    }
);

export const Authorization = ({
  policyCheck,
  allowedRoles,
  forbiddenFallback = null,
  children,
}: AuthorizationProps) => {
  const { checkAccess } = useAuthorization();
  let canAccess = false;
  if (allowedRoles) {
    canAccess = checkAccess({ allowedRoles });
  }
  if (typeof policyCheck !== "undefined") {
    canAccess = policyCheck;
  }

  return <>{canAccess ? children : forbiddenFallback}</>;
};
